import { Navigate, createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';

import { AppSkeletonLoader } from '@/components/AppSkeletonLoader';
import { RequireEntitlements } from '@/components/Auth/RequireEntitlements';
import { RequireFeatureFlag } from '@/components/Auth/RequireFeatureFlag';
import { Forbidden } from '@/components/Errors/Forbidden';
import { InvestigationMetricsLayout } from '@/components/InvestigationMetrics/InvestigationMetricsLayout';
import { InvestigationMetricsPage } from '@/components/InvestigationMetrics/InvestigationMetricsPage';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useUser } from '@/hooks/useUser';
import { investigationMetricsSearchSchema } from '@/utils/investigationMetrics';

export const Route = createFileRoute('/$organizationName/investigation-metrics')({
  validateSearch: zodValidator(investigationMetricsSearchSchema),
  component: GatedInvestigationMetricsPage,
});

function GatedInvestigationMetricsPage() {
  const { isDriverSupport, routeOrganizationName } = useUser();

  if (isDriverSupport) {
    return <InvestigationMetricsPageWithTitle />;
  }

  return (
    <RequireFeatureFlag
      flag={FEATURE_FLAGS.INVESTIGATION_METRICS}
      loading={<AppSkeletonLoader />}
      redirect={
        <Navigate to="/$organizationName/sources" replace params={{ organizationName: routeOrganizationName }} />
      }
    >
      <RequireEntitlements
        admins={['isSuperAdmin']}
        loading={<AppSkeletonLoader />}
        fallback={
          <InvestigationMetricsLayout>
            <Forbidden />
          </InvestigationMetricsLayout>
        }
      >
        <InvestigationMetricsPageWithTitle />
      </RequireEntitlements>
    </RequireFeatureFlag>
  );
}

function InvestigationMetricsPageWithTitle() {
  const { organizationDisplayName } = useUser();

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Investigation Metrics`;
  }, [organizationDisplayName]);

  return (
    <InvestigationMetricsLayout>
      <InvestigationMetricsPage />
    </InvestigationMetricsLayout>
  );
}
